import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../outils';
import { Icone, type NomIcone } from './Icone';
import { raccourciAria, Touche } from './Touche';

export interface BoutonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /**
   * principal (un seul par vue) | secondaire (verre) | colore (verre coloré : un ou deux par
   * écran) | discret | danger | danger-discret.
   */
  variante?: 'principal' | 'secondaire' | 'colore' | 'discret' | 'danger' | 'danger-discret';
  /** normale 44 px | compacte 36 px | petite 28 px. */
  taille?: 'normale' | 'compacte' | 'petite';
  icone?: NomIcone;
  /** Bouton carré sans libellé visible : `etiquette` devient aria-label et infobulle. */
  iconeSeule?: boolean;
  etiquette?: string;
  /** Touche affichée à droite, recopiée dans aria-keyshortcuts. */
  raccourci?: string;
  /** Opération en cours : l'icône laisse place à l'indicateur de rotation. */
  occupe?: boolean;
  desactive?: boolean;
  /** Désactivé mais atteignable au clavier (aria-disabled). */
  focalisableDesactive?: boolean;
  /** État enfoncé d'un bouton à bascule (aria-pressed). */
  enfonce?: boolean;
  large?: boolean;
  children?: ReactNode;
}

/** Déclenche une action ; le libellé dit exactement ce qui se passe. */
export const Bouton = forwardRef<HTMLButtonElement, BoutonProps>(function Bouton(
  {
    variante = 'secondaire',
    taille = 'normale',
    icone,
    iconeSeule = false,
    etiquette,
    raccourci,
    occupe = false,
    desactive = false,
    focalisableDesactive = false,
    enfonce,
    large = false,
    className,
    children,
    type = 'button',
    onClick,
    title,
    ...reste
  },
  ref,
) {
  const inactif = desactive || occupe;
  const infobulle =
    title ??
    (iconeSeule && etiquette ? etiquette + (raccourci ? ` (${raccourci})` : '') : undefined);
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'fc-bouton',
        `fc-bouton--${variante}`,
        taille === 'compacte' && 'fc-bouton--compact',
        taille === 'petite' && 'fc-bouton--petit',
        iconeSeule && 'fc-bouton--icone',
        large && 'fc-bouton--large',
        className,
      )}
      disabled={desactive && !focalisableDesactive ? true : undefined}
      aria-disabled={desactive && focalisableDesactive ? true : undefined}
      aria-busy={occupe ? true : undefined}
      aria-pressed={enfonce}
      aria-label={iconeSeule ? etiquette : undefined}
      aria-keyshortcuts={raccourci ? raccourciAria(raccourci) : undefined}
      title={infobulle}
      onClick={inactif ? undefined : onClick}
      {...reste}
    >
      {occupe ? (
        <span className="fc-rotation" aria-hidden="true" />
      ) : (
        icone !== undefined && <Icone nom={icone} />
      )}
      {!iconeSeule && children}
      {raccourci !== undefined && !iconeSeule && <Touche>{raccourci}</Touche>}
    </button>
  );
});
